import { useMemo, useState, type FormEvent, type ReactNode } from "react"
import type {
  CreatePartnershipContentRequest,
  CreatePartnershipRequest,
  CreatePartnershipRowFromAdRequest,
  LinkPartnershipAdRequest,
  PartnershipAdDto,
  PartnershipContentRowDto,
  PartnershipDto,
  PartnershipPlatform,
  PartnershipRightsStatus,
  PartnershipWindowAnchor,
} from "~/ui-rtk/api/types/partnerships.types"
// Direct imports (not the `common` barrel) — see CreativesTab.tsx.
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import Input from "~/ui-rtk/components/ui/controls/Input"
import Select from "~/ui-rtk/components/ui/controls/Select"
import {
  PLATFORM_OPTIONS,
  RIGHTS_STATUS_OPTIONS,
  WINDOW_ANCHOR_OPTIONS,
} from "./partnership-copy"

/**
 * The tracker's modal forms. Every dialog is mounted only while open so its
 * form state starts fresh each time; `onSubmit` resolves to `true` on success
 * (the tab owns the mutation + toast) and the dialog closes itself.
 */

const DIALOG_CONTAINER_CLS =
  "flex max-h-[90vh] w-full max-w-[95vw] flex-col rounded-lg border border-basic-blue bg-basic-dark-blue p-5 text-left text-white shadow-xl overflow-y-auto"

function optional(value: string): string | null {
  const trimmed = value.trim()
  return trimmed ? trimmed : null
}

function optionalNumber(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

interface FieldProps {
  label: string
  hint?: string
  children: ReactNode
}
function Field({ label, hint, children }: FieldProps) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-300">
      <span>
        {label}
        {hint && <span className="ml-1 text-gray-500">({hint})</span>}
      </span>
      {children}
    </label>
  )
}

interface DialogFrameProps {
  title: string
  description?: string
  onClose: () => void
  onSubmit: () => void
  submitLabel: string
  submitting: boolean
  error: string | null
  width?: number
  children: ReactNode
}
function DialogFrame({
  title,
  description,
  onClose,
  onSubmit,
  submitLabel,
  submitting,
  error,
  width = 520,
  children,
}: DialogFrameProps) {
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    onSubmit()
  }
  return (
    <Dialog
      isOpen
      handleClose={onClose}
      closeOnClickOutside
      width={width}
      classes={{ container: DIALOG_CONTAINER_CLS }}
    >
      <form
        onSubmit={handleSubmit}
        onKeyDown={e => {
          if (e.key === "Escape") onClose()
        }}
        className="flex flex-col gap-3"
      >
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          {description && (
            <p className="mt-1 text-xs text-gray-400">{description}</p>
          )}
        </div>
        {children}
        {error && <p className="text-xs text-states-error">{error}</p>}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn btn-ghost btn-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary btn-sm"
          >
            {submitting && (
              <span className="loading loading-spinner loading-xs" />
            )}
            {submitLabel}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

/* ----- Add creator ----- */

export interface AddCreatorDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (body: CreatePartnershipRequest) => Promise<boolean>
}
function AddCreatorForm({
  onClose,
  onSubmit,
}: Omit<AddCreatorDialogProps, "open">) {
  const [platform, setPlatform] = useState<PartnershipPlatform>("INSTAGRAM")
  const [handle, setHandle] = useState("")
  const [displayName, setDisplayName] = useState("")
  const [contactName, setContactName] = useState("")
  const [contactEmail, setContactEmail] = useState("")
  const [productName, setProductName] = useState("")
  const [rightsStatus, setRightsStatus] =
    useState<PartnershipRightsStatus>("AGREEMENT_PENDING")
  const [usageDays, setUsageDays] = useState("")
  const [anchor, setAnchor] = useState<PartnershipWindowAnchor>("FIRST_SPEND")
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")
  const [renewalRate, setRenewalRate] = useState("")
  const [paidBudget, setPaidBudget] = useState("")
  const [landingPageUrl, setLandingPageUrl] = useState("")
  const [notes, setNotes] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    const creatorHandle = handle.trim().replace(/^@+/, "")
    if (!creatorHandle) {
      setError("Creator handle is required")
      return
    }
    const days = optionalNumber(usageDays)
    if (
      usageDays.trim() &&
      (days === null || !Number.isInteger(days) || days < 1)
    ) {
      setError("Usage days must be a whole number of days")
      return
    }
    setError(null)
    setSubmitting(true)
    const ok = await onSubmit({
      platform,
      creatorHandle,
      creatorDisplayName: optional(displayName),
      contactName: optional(contactName),
      contactEmail: optional(contactEmail),
      productName: optional(productName),
      rightsStatus,
      usageDays: days,
      windowAnchor: anchor,
      startDate: startDate || null,
      endDate: endDate || null,
      renewalRate: optionalNumber(renewalRate),
      paidBudget: optionalNumber(paidBudget),
      landingPageUrl: optional(landingPageUrl),
      notes: optional(notes),
    })
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <DialogFrame
      title="Add creator"
      description="A creator deal that posts can be linked to. Manual rows are for creators Meta discovery hasn't returned yet (TikTok, pre-post briefs)."
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Add creator"
      submitting={submitting}
      error={error}
      width={600}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Platform">
          <Select
            aria-label="Platform"
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={e => setPlatform(e.target.value as PartnershipPlatform)}
            className="h-9 text-sm"
          />
        </Field>
        <Field label="Creator handle">
          <Input
            fullWidth
            aria-label="Creator handle"
            placeholder="@handle"
            value={handle}
            onChange={e => setHandle(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Display name" hint="optional">
          <Input
            fullWidth
            aria-label="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
          />
        </Field>
        <Field label="Product / deal" hint="optional">
          <Input
            fullWidth
            aria-label="Product"
            value={productName}
            onChange={e => setProductName(e.target.value)}
          />
        </Field>
        <Field label="Contact name" hint="optional">
          <Input
            fullWidth
            aria-label="Contact name"
            value={contactName}
            onChange={e => setContactName(e.target.value)}
          />
        </Field>
        <Field label="Contact email" hint="optional">
          <Input
            fullWidth
            type="email"
            aria-label="Contact email"
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
          />
        </Field>
        <Field label="Rights status">
          <Select
            aria-label="Rights status"
            options={RIGHTS_STATUS_OPTIONS}
            value={rightsStatus}
            onChange={e =>
              setRightsStatus(e.target.value as PartnershipRightsStatus)
            }
            className="h-9 text-sm"
          />
        </Field>
        <Field label="Usage days" hint="optional">
          <Input
            fullWidth
            type="number"
            min={1}
            max={3650}
            aria-label="Usage days"
            value={usageDays}
            onChange={e => setUsageDays(e.target.value)}
          />
        </Field>
        <Field label="Window starts">
          <Select
            aria-label="Window anchor"
            options={WINDOW_ANCHOR_OPTIONS}
            value={anchor}
            onChange={e => setAnchor(e.target.value as PartnershipWindowAnchor)}
            className="h-9 text-sm"
          />
        </Field>
        <div />
        <Field label="Start date" hint="optional">
          <Input
            fullWidth
            type="date"
            aria-label="Start date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </Field>
        <Field label="End date" hint="optional">
          <Input
            fullWidth
            type="date"
            aria-label="End date"
            value={endDate}
            min={startDate || undefined}
            onChange={e => setEndDate(e.target.value)}
          />
        </Field>
        <Field label="Renewal rate" hint="optional">
          <Input
            fullWidth
            type="number"
            min={0}
            step="0.01"
            aria-label="Renewal rate"
            value={renewalRate}
            onChange={e => setRenewalRate(e.target.value)}
          />
        </Field>
        <Field label="Paid budget" hint="optional">
          <Input
            fullWidth
            type="number"
            min={0}
            step="0.01"
            aria-label="Paid budget"
            value={paidBudget}
            onChange={e => setPaidBudget(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Landing page URL" hint="optional">
        <Input
          fullWidth
          type="url"
          aria-label="Landing page URL"
          placeholder="https://"
          value={landingPageUrl}
          onChange={e => setLandingPageUrl(e.target.value)}
        />
      </Field>
      <Field label="Notes" hint="optional">
        <textarea
          aria-label="Notes"
          value={notes}
          rows={3}
          maxLength={2000}
          onChange={e => setNotes(e.target.value)}
          className="textarea textarea-bordered w-full"
        />
      </Field>
    </DialogFrame>
  )
}

export function AddCreatorDialog({
  open,
  onClose,
  onSubmit,
}: AddCreatorDialogProps) {
  if (!open) return null
  return <AddCreatorForm onClose={onClose} onSubmit={onSubmit} />
}

/* ----- Add content ----- */

export interface AddContentDialogProps {
  open: boolean
  partnerships: PartnershipDto[]
  onClose: () => void
  onSubmit: (body: CreatePartnershipContentRequest) => Promise<boolean>
}
const POST_TYPE_OPTIONS = [
  { value: "", label: "Post type (optional)" },
  { value: "reels", label: "Reel" },
  { value: "feed", label: "Feed post" },
  { value: "story", label: "Story" },
  { value: "video", label: "Video" },
]

function AddContentForm({
  partnerships,
  onClose,
  onSubmit,
}: Omit<AddContentDialogProps, "open">) {
  const [platform, setPlatform] = useState<PartnershipPlatform>("INSTAGRAM")
  const [partnershipId, setPartnershipId] = useState("")
  const [permalink, setPermalink] = useState("")
  const [postType, setPostType] = useState("")
  const [adCode, setAdCode] = useState("")
  const [landingPageUrl, setLandingPageUrl] = useState("")
  const [notes, setNotes] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const creatorOptions = useMemo(
    () => [
      { value: "", label: "No creator yet" },
      ...partnerships
        .filter(p => p.platform === platform)
        .map(p => ({
          value: p.id,
          label: `@${p.creatorHandle}${p.creatorDisplayName ? ` — ${p.creatorDisplayName}` : ""}`,
        })),
    ],
    [partnerships, platform],
  )

  const submit = async () => {
    const link = optional(permalink)
    const code = optional(adCode)
    if (!link && !code) {
      setError("Add the post link or an ad code")
      return
    }
    setError(null)
    setSubmitting(true)
    const ok = await onSubmit({
      platform,
      partnershipId: partnershipId || null,
      permalink: link,
      adCode: code,
      postType: optional(postType),
      landingPageUrl: optional(landingPageUrl),
      notes: optional(notes),
    })
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <DialogFrame
      title="Add content"
      description="Track a post by hand. Once Meta discovery returns the same permalink the row upgrades itself to a discovered post."
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Add content"
      submitting={submitting}
      error={error}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Platform">
          <Select
            aria-label="Platform"
            options={PLATFORM_OPTIONS}
            value={platform}
            onChange={e => {
              setPlatform(e.target.value as PartnershipPlatform)
              setPartnershipId("")
            }}
            className="h-9 text-sm"
          />
        </Field>
        <Field label="Creator">
          <Select
            aria-label="Creator"
            options={creatorOptions}
            value={partnershipId}
            onChange={e => setPartnershipId(e.target.value)}
            className="h-9 text-sm"
          />
        </Field>
      </div>
      <Field label="Post link">
        <Input
          fullWidth
          type="url"
          aria-label="Post link"
          placeholder="https://www.instagram.com/reel/…"
          value={permalink}
          onChange={e => setPermalink(e.target.value)}
          autoFocus
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Post type">
          <Select
            aria-label="Post type"
            options={POST_TYPE_OPTIONS}
            value={postType}
            onChange={e => setPostType(e.target.value)}
            className="h-9 text-sm"
          />
        </Field>
        <Field label="Ad code" hint="optional, stored encrypted">
          <Input
            fullWidth
            type="password"
            autoComplete="off"
            aria-label="Ad code"
            value={adCode}
            onChange={e => setAdCode(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Landing page URL" hint="optional">
        <Input
          fullWidth
          type="url"
          aria-label="Landing page URL"
          placeholder="https://"
          value={landingPageUrl}
          onChange={e => setLandingPageUrl(e.target.value)}
        />
      </Field>
      <Field label="Notes" hint="optional">
        <textarea
          aria-label="Notes"
          value={notes}
          rows={3}
          maxLength={2000}
          onChange={e => setNotes(e.target.value)}
          className="textarea textarea-bordered w-full"
        />
      </Field>
    </DialogFrame>
  )
}

export function AddContentDialog({
  open,
  partnerships,
  onClose,
  onSubmit,
}: AddContentDialogProps) {
  if (!open) return null
  return (
    <AddContentForm
      partnerships={partnerships}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}

/* ----- Link an unmatched ad to a content row ----- */

export interface LinkAdDialogProps {
  /** The unmatched ad being linked (null = closed). */
  ad: PartnershipAdDto | null
  contentRows: PartnershipContentRowDto[]
  onClose: () => void
  onSubmit: (adId: string, body: LinkPartnershipAdRequest) => Promise<boolean>
}
interface ContentGroup {
  label: string
  rows: PartnershipContentRowDto[]
}

/** Content rows grouped by creator (handle), unlinked rows last. */
function groupByCreator(rows: PartnershipContentRowDto[]): ContentGroup[] {
  const groups = new Map<string, ContentGroup>()
  for (const row of rows) {
    const key = row.partnership ? `@${row.partnership.creatorHandle}` : "~"
    const label = row.partnership
      ? `@${row.partnership.creatorHandle}`
      : "No creator linked"
    const group = groups.get(key) ?? { label, rows: [] }
    group.rows.push(row)
    groups.set(key, group)
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, g]) => g)
}

function contentOptionLabel(row: PartnershipContentRowDto): string {
  const caption = row.caption?.trim()
  let text = caption || (row.permalink ?? row.contentId ?? row.id)
  if (text.length > 50) text = `${text.slice(0, 49)}…`
  const when = row.postedAt ? ` · ${row.postedAt.slice(0, 10)}` : ""
  return `${text}${when}`
}

function LinkAdForm({
  ad,
  contentRows,
  onClose,
  onSubmit,
}: LinkAdDialogProps & { ad: PartnershipAdDto }) {
  const [contentId, setContentId] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const groups = useMemo(() => groupByCreator(contentRows), [contentRows])

  const submit = async () => {
    if (!contentId) {
      setError("Pick the post this ad promotes")
      return
    }
    setError(null)
    setSubmitting(true)
    const ok = await onSubmit(ad.id, { partnershipContentId: contentId })
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <DialogFrame
      title="Link ad to a post"
      description={`${ad.adName} — pick the tracked post this ad promotes. The creator is copied from the post.`}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Link ad"
      submitting={submitting}
      error={error}
    >
      <Field label="Tracked post">
        {/* Raw <select>: the shared Select has no <optgroup> support and the
         * creator grouping is the whole point here. Default height, so the
         * forms-plugin padding clip (CLAUDE.md gotcha #9) doesn't apply. */}
        <select
          aria-label="Tracked post"
          value={contentId}
          onChange={e => setContentId(e.target.value)}
          className="select w-full border border-basic-blue bg-basic-dark-blue py-0"
          autoFocus
        >
          <option value="">Choose a post…</option>
          {groups.map(group => (
            <optgroup key={group.label} label={group.label}>
              {group.rows.map(row => (
                <option key={row.id} value={row.id}>
                  {contentOptionLabel(row)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>
      {contentRows.length === 0 && (
        <p className="text-xs text-gray-400">
          No tracked posts yet — use &quot;Create row&quot; instead.
        </p>
      )}
    </DialogFrame>
  )
}

export function LinkAdDialog({
  ad,
  contentRows,
  onClose,
  onSubmit,
}: LinkAdDialogProps) {
  if (!ad) return null
  return (
    <LinkAdForm
      ad={ad}
      contentRows={contentRows}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  )
}

/* ----- Create a tracker row from an unmatched ad ----- */

export interface CreateRowFromAdDialogProps {
  ad: PartnershipAdDto | null
  onClose: () => void
  onSubmit: (
    adId: string,
    body: CreatePartnershipRowFromAdRequest,
  ) => Promise<boolean>
}
/**
 * Ad names usually end with the creator ("…_Reel_@alice", "… | alice"), so
 * the last separator-delimited token is the best default handle guess.
 */
export function guessHandleFromAdName(adName: string): string {
  const tokens = adName
    .split(/[\s|/_-]+/)
    .map(t => t.trim())
    .filter(Boolean)
  const last = tokens[tokens.length - 1] ?? ""
  return last.replace(/^@+/, "")
}

function CreateRowFromAdForm({
  ad,
  onClose,
  onSubmit,
}: CreateRowFromAdDialogProps & { ad: PartnershipAdDto }) {
  const [platform, setPlatform] = useState<PartnershipPlatform>("INSTAGRAM")
  const [handle, setHandle] = useState(() => guessHandleFromAdName(ad.adName))
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async () => {
    const creatorHandle = handle.trim().replace(/^@+/, "")
    if (!creatorHandle) {
      setError("Creator handle is required")
      return
    }
    setError(null)
    setSubmitting(true)
    const ok = await onSubmit(ad.id, { platform, creatorHandle })
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <DialogFrame
      title="Create tracker row"
      description={`${ad.adName} — creates the creator (if new) and a manual post row from the ad's source media, then links the ad to it.`}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Create row"
      submitting={submitting}
      error={error}
      width={440}
    >
      <Field label="Platform">
        <Select
          aria-label="Platform"
          options={PLATFORM_OPTIONS}
          value={platform}
          onChange={e => setPlatform(e.target.value as PartnershipPlatform)}
          className="h-9 text-sm"
        />
      </Field>
      <Field label="Creator handle">
        <Input
          fullWidth
          aria-label="Creator handle"
          placeholder="@handle"
          value={handle}
          onChange={e => setHandle(e.target.value)}
          autoFocus
        />
      </Field>
    </DialogFrame>
  )
}

export function CreateRowFromAdDialog({
  ad,
  onClose,
  onSubmit,
}: CreateRowFromAdDialogProps) {
  if (!ad) return null
  return <CreateRowFromAdForm ad={ad} onClose={onClose} onSubmit={onSubmit} />
}
